import styled from '@emotion/styled';
import { tokens, Icon, IconButton } from '@hotjar/hotsauce';
import { useLocalStorageJSONValue } from '@hotjar/insights-common/hooks/useStorage';
import { forwardRef, useEffect } from 'react';
import { useNavigation } from '@hotjar/navigation';

const WIDGET_Z_INDEX = 9990;
const WIDTH = 400;

const Wrapper = styled.aside<{ position: 'left' | 'right'; isSidebarMinimized: boolean }>`
  position: fixed;
  z-index: ${WIDGET_Z_INDEX + 1};
  bottom: ${tokens.spacer.x2};

  ${({ position, isSidebarMinimized }) => {
    if (position === 'left') {
      const left = isSidebarMinimized ? tokens.spacer.x8 : '220px';

      return `
      transition: left 300ms ease-in-out;
      left: calc(${left} + ${tokens.spacer.x1});
      `;
    }

    return `right: ${tokens.spacer.x8};`;
  }}
`;

const Container = styled.div<{ isCollapsed: boolean | null }>`
  overflow: hidden;
  position: relative;
  background: ${tokens.BRAND_COLORS.cobalt_100};
  border-radius: ${tokens.radius.l};
  transition: max-width 400ms ease-in-out, max-height 400ms ease-in-out;
  border: 1px solid ${tokens.color.ui_border_light};
  ${tokens.elevation(2)};

  ${({ isCollapsed }) =>
    isCollapsed ?? false
      ? `
  max-width: 250px;
  max-height: 40px;

  &:focus-within {
    ${tokens.focus()};
  }
  `
      : `
  max-width: ${WIDTH}px;
  max-height: calc(100vh - 140px);
  display: flex;
  flex-direction: column;

  &:before {
    content: '';
    display: block;
    position: absolute;
    width: 262px;
    height: 262px;
    right: -140px;
    top: 0;
    z-index: 0;

    background: radial-gradient(
      105% 117.79% at 48.77% 80.34%,
      rgba(161, 143, 214, 0.5) 8.43%,
      rgba(195, 240, 247, 0.5) 58.85%,
      rgba(195, 240, 247, 0) 100%
    );
    mix-blend-mode: darken;
    filter: blur(100px);
    transform: rotate(-30deg);
  }
  `}
`;

const Header = styled.header<{ isCollapsed: boolean | null }>`
  display: flex;
  align-items: center;
  border: 0;
  width: 100%;
  box-shadown: 0;
  background: none;
  text-align: left;
  cursor: pointer;
  position: relative;
  z-index: 1;

  ${({ isCollapsed }) =>
    isCollapsed ?? false
      ? `
    padding: ${tokens.spacer.x1} ${tokens.spacer.x2};

    &:focus {
      outline: 0;
    }
    `
      : `
    padding: ${tokens.spacer.x2} 6px ${tokens.spacer.x2} ${tokens.spacer.x2};
    `}
`;

const Title = styled.span<{ isCollapsed: boolean | null }>`
  flex-grow: 1;
  margin: 0;
  ${tokens.truncateText()}
  ${tokens.text('caption')}
  text-transform: uppercase;
  ${({ isCollapsed }) =>
    isCollapsed ?? false
      ? `
          text-transform: unset;
          ${tokens.text('header5')}
          font-size: 14px;
        `
      : ''};
  transition: font-size 100ms ease-in-out;
`;

const Content = styled.div`
  display: block;
  position: relative;
  z-index: 1;
  padding: 0 ${tokens.spacer.x1} ${tokens.spacer.x2};
  width: ${WIDTH}px;
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
`;

const Badge = styled.span`
  background: ${tokens.color.primary_cta};
  font-size: ${tokens.text('subtitle3')};
  color: ${tokens.color.white};
  width: ${tokens.spacer.x2};
  height: ${tokens.spacer.x2};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -${tokens.spacer.x1};
  right: -${tokens.spacer.x1};
`;

const SuccessBadge = styled(Icon)`
  position: absolute;
  top: -${tokens.spacer.x1};
  right: -${tokens.spacer.x1};
`;

export interface FloatingPanelProps {
  title: string;
  remainingTasks: number;
  children: React.ReactNode;
  collapsedStateKey: string;
  position?: 'left' | 'right';
  onDismiss?: () => void;
  onToggle?: (isCollapsed: boolean) => void;
}

export const FloatingPanel = forwardRef<HTMLElement, FloatingPanelProps>(
  (
    { title, remainingTasks, onDismiss, onToggle, collapsedStateKey, children, position = 'right' },
    ref,
  ): JSX.Element => {
    const [isCollapsed, setIsCollapsed] = useLocalStorageJSONValue<boolean>(collapsedStateKey);
    const onDismissClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
      if (onDismiss) {
        e.stopPropagation();
        onDismiss();
      }
    };
    const onHeaderKeyPress = (e: React.KeyboardEvent<HTMLHeadingElement>) => {
      if (e.key === 'Enter') {
        setIsCollapsed(!(isCollapsed ?? false));
      }
    };

    useEffect(() => {
      if (onToggle) {
        onToggle(!!(isCollapsed ?? false));
      }
    }, [isCollapsed, onToggle]);

    const { isSidebarMinimized } = useNavigation();

    return (
      <Wrapper position={position} isSidebarMinimized={isSidebarMinimized} ref={ref}>
        <Container isCollapsed={isCollapsed}>
          <Header
            tabIndex={isCollapsed ?? false ? 0 : undefined}
            role={isCollapsed ?? false ? 'button' : undefined}
            isCollapsed={isCollapsed}
            onKeyPress={isCollapsed ?? false ? onHeaderKeyPress : undefined}
            onClick={() => setIsCollapsed(!(isCollapsed ?? false))}
          >
            <Title isCollapsed={isCollapsed}>{title}</Title>
            {onDismiss && !(isCollapsed ?? false) && (
              <IconButton
                icon="close"
                kind="ghost"
                size="s"
                title="Dismiss"
                tooltipText="Dismiss"
                onClick={onDismissClick}
              />
            )}
            {!(isCollapsed ?? false) && (
              <IconButton
                icon="chevron_bottom"
                kind="ghost"
                size="s"
                tooltipText="Minimize"
                title="Minimize"
              />
            )}
          </Header>
          {!(isCollapsed ?? false) && <Content>{children}</Content>}
        </Container>
        {(isCollapsed ?? false) && (
          <>
            {remainingTasks > 0 ? (
              <Badge>{remainingTasks}</Badge>
            ) : (
              <SuccessBadge icon="check_alt" color="text_success" />
            )}
          </>
        )}
      </Wrapper>
    );
  },
);
